import { Checkbox, Popover } from 'antd';
import React from 'react';
import { LuColumns } from 'react-icons/lu';

export interface ColumnVisibilityGroup<T extends string> {
  /** Label for the group's own parent checkbox (e.g. "Costs") — toggling it shows/hides every member at once. */
  label: string;
  /** Member column keys, rendered indented directly under the group's parent checkbox, in the order given here. */
  keys: T[];
}

export interface ColumnVisibilityControlProps<T extends string> {
  /** Every toggleable column, in display order. A column that belongs to a group (see `groups`) renders once, nested under that group, at the position of its first occurrence here — later occurrences of the same key are skipped. */
  columns: T[];
  /** Resolves a column key to its picker label. */
  getLabel: (key: T) => string;
  groups?: ColumnVisibilityGroup<T>[];
  hidden: T[];
  onToggle: (key: T) => void;
  onSetHidden: (keys: T[]) => void;
  onShowAll: () => void;
  /** Extra classes on the trigger button itself — e.g. `ml-auto` to pin this control (and whatever follows it in the same flex row) to one side. */
  className?: string;
}

/** A checkbox row that highlights on hover across its full width, not just the checkbox itself. */
const PickerRow: React.FC<{
  indent?: boolean;
  checked: boolean;
  indeterminate?: boolean;
  onChange: () => void;
  children: React.ReactNode;
}> = ({ indent, checked, indeterminate, onChange, children }) => (
  <label
    className={`flex cursor-pointer items-center rounded px-2 py-1 hover:bg-rowHover ${indent ? 'ml-4' : ''}`}
  >
    <Checkbox checked={checked} indeterminate={indeterminate} onChange={onChange}>
      {children}
    </Checkbox>
  </label>
);

/**
 * "Columns" button + click-to-open picker for show/hide control over a
 * table's optional columns — a ghost pill matching this app's other
 * SectionCard action buttons, opening an antd Popover with one checkbox row
 * per column. `groups` (e.g. a "Costs" cluster) get a parent checkbox that's
 * indeterminate when only some members are hidden, and toggling it shows or
 * hides every member together. Generic over the column-key type so each
 * caller supplies its own string-literal union (e.g. `AnalysisColumnKey`,
 * a Farm Diagnostics column key) — this component holds no domain knowledge
 * of what a "column" means, only how to list and toggle them.
 */
function ColumnVisibilityControl<T extends string>({
  columns,
  getLabel,
  groups = [],
  hidden,
  onToggle,
  onSetHidden,
  onShowAll,
  className,
}: ColumnVisibilityControlProps<T>) {
  const hiddenSet = new Set(hidden);
  const groupByKey = new Map<T, ColumnVisibilityGroup<T>>();
  for (const group of groups) {
    for (const key of group.keys) groupByKey.set(key, group);
  }
  const renderedGroups = new Set<ColumnVisibilityGroup<T>>();

  const content = (
    <div className="flex max-h-96 w-56 flex-col gap-0.5 overflow-y-auto p-1">
      <button
        type="button"
        className="mb-1 cursor-pointer self-start border-none bg-transparent p-0 text-xs font-semibold text-charcoal hover:underline disabled:cursor-default disabled:text-gray-muted disabled:no-underline"
        onClick={onShowAll}
        disabled={hidden.length === 0}
      >
        Show all
      </button>
      {columns.map((key) => {
        const group = groupByKey.get(key);
        if (group) {
          if (renderedGroups.has(group)) return null;
          renderedGroups.add(group);
          const allHidden = group.keys.every((k) => hiddenSet.has(k));
          const someHidden = group.keys.some((k) => hiddenSet.has(k));
          const handleToggleGroup = () => {
            const withoutGroup = hidden.filter((k) => !group.keys.includes(k));
            onSetHidden(allHidden ? withoutGroup : [...withoutGroup, ...group.keys]);
          };
          return (
            <React.Fragment key={group.label}>
              <PickerRow
                checked={!someHidden}
                indeterminate={someHidden && !allHidden}
                onChange={handleToggleGroup}
              >
                {group.label}
              </PickerRow>
              {group.keys.map((memberKey) => (
                <PickerRow
                  key={memberKey}
                  indent
                  checked={!hiddenSet.has(memberKey)}
                  onChange={() => onToggle(memberKey)}
                >
                  {getLabel(memberKey)}
                </PickerRow>
              ))}
            </React.Fragment>
          );
        }
        return (
          <PickerRow key={key} checked={!hiddenSet.has(key)} onChange={() => onToggle(key)}>
            {getLabel(key)}
          </PickerRow>
        );
      })}
    </div>
  );

  return (
    <Popover trigger="click" placement="bottomRight" content={content}>
      <button
        type="button"
        className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-transparent bg-transparent px-2.5 py-1 text-sm font-semibold text-charcoal transition-colors hover:border-charcoal hover:bg-rowHover ${className ?? ''}`}
      >
        <LuColumns size={14} />
        Columns
        {hidden.length > 0 && (
          <span className="text-xs font-normal text-gray-muted">({hidden.length} hidden)</span>
        )}
      </button>
    </Popover>
  );
}

export default ColumnVisibilityControl;
